import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Fetcher } from '@this/src/util';
import OnlineApplication from '@this/src/domain/online_application/online_application';
import type { ApplicationData } from './types';

interface UseApplicationDataProps {
  token: string;
  endpoint?: string;
  redirectOnError?: boolean;
}

interface UseApplicationDataReturn {
  loading: boolean;
  application: OnlineApplication | null;
  contactName: string;
  contactEmail: string;
  contactTel: string;
  startDesiredMonth: string;
  errorMessage: string;
  setErrorMessage: (message: string) => void;
}

export const useApplicationData = ({
  token,
  endpoint = `/online_applications/token/${token}.json`,
  redirectOnError = false
}: UseApplicationDataProps): UseApplicationDataReturn => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [application, setApplication] = useState<OnlineApplication | null>(null);
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactTel, setContactTel] = useState('');
  const [startDesiredMonth, setStartDesiredMonth] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      if (!token) {
        if (redirectOnError) {
          history.push('/');
        }
        return;
      }

      try {
        const response = await Fetcher.get<{ success: boolean; data: ApplicationData; error?: string }>(endpoint);

        if (response.success && response.data) {
          const app = new OnlineApplication(response.data);
          setApplication(app);

          const firstCompany = response.data.companies[0];
          if (firstCompany) {
            setContactName(firstCompany.contact_name || '');
            setContactEmail(firstCompany.contact_email || '');
            setContactTel(firstCompany.contact_tel || '');
          }
          if (response.data.start_desired_month) {
            setStartDesiredMonth(response.data.start_desired_month);
          }
        } else {
          setErrorMessage(response.error || 'データの取得に失敗しました');
          if (redirectOnError) {
            history.push('/');
          }
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        setErrorMessage('データの取得に失敗しました');
        if (redirectOnError) {
          history.push('/');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token, endpoint, history, redirectOnError]);

  return {
    loading,
    application,
    contactName,
    contactEmail,
    contactTel,
    startDesiredMonth,
    errorMessage,
    setErrorMessage
  };
};
